/**
 * Icon compatibility layer — wraps @phosphor-icons/react with Lucide-compatible names/API.
 *
 * Phosphor uses a `size` prop; Lucide uses className (h-4 w-4 etc.).
 * The wrapper reads className to pick the right pixel size so both usages work.
 */
import React from 'react'
import {
  MagnifyingGlass,
  X as PhosphorX,
  CaretDown,
  CaretUp,
  CaretRight,
  CaretLeft,
  ArrowsOutSimple,
  Check as PhosphorCheck,
  Circle as PhosphorCircle,
  DotsThreeVertical,
  DotsThree,
  Question,
  ArrowLeft as PhosphorArrowLeft,
  ArrowRight as PhosphorArrowRight,
  ArrowCounterClockwise,
  CircleNotch,
  CheckCircle as PhosphorCheckCircle,
  XCircle as PhosphorXCircle,
  ThumbsUp as PhosphorThumbsUp,
  ThumbsDown as PhosphorThumbsDown,
  PencilSimple,
  TrashSimple,
  QrCode as PhosphorQrCode,
  ClipboardText,
  FileText as PhosphorFileText,
  WarningCircle,
  Warning as PhosphorWarning,
  Copy as PhosphorCopy,
  CalendarBlank,
  User as PhosphorUser,
  ChevronDown as PhosphorChevronDown,
  Gear,
  SignOut,
  House,
} from '@phosphor-icons/react'

const createIconWrapper = (PhosphorIcon, displayName) => {
  const Wrapper = React.forwardRef((props, ref) => {
    const { className, size, weight, ...rest } = props
    let iconSize = size || 16
    if (className) {
      if (className.includes('w-3') || className.includes('h-3')) iconSize = 12
      else if (className.includes('w-4') || className.includes('h-4')) iconSize = 16
      else if (className.includes('w-5') || className.includes('h-5')) iconSize = 20
      else if (className.includes('w-6') || className.includes('h-6')) iconSize = 24
      else if (className.includes('w-8') || className.includes('h-8')) iconSize = 32
    }
    return (
      <PhosphorIcon
        ref={ref}
        size={iconSize}
        weight={weight || 'regular'}
        className={className}
        {...rest}
      />
    )
  })
  if (displayName) Wrapper.displayName = displayName
  return Wrapper
}

export const Search = createIconWrapper(MagnifyingGlass, 'Search')
export const X = createIconWrapper(PhosphorX, 'X')
export const ChevronDown = createIconWrapper(CaretDown, 'ChevronDown')
export const ChevronUp = createIconWrapper(CaretUp, 'ChevronUp')
export const ChevronRight = createIconWrapper(CaretRight, 'ChevronRight')
export const ChevronLeft = createIconWrapper(CaretLeft, 'ChevronLeft')
export const ChevronsUpDown = createIconWrapper(ArrowsOutSimple, 'ChevronsUpDown')
export const Check = createIconWrapper(PhosphorCheck, 'Check')
export const Circle = createIconWrapper(PhosphorCircle, 'Circle')
export const MoreVertical = createIconWrapper(DotsThreeVertical, 'MoreVertical')
export const MoreHorizontal = createIconWrapper(DotsThree, 'MoreHorizontal')
export const HelpCircle = createIconWrapper(Question, 'HelpCircle')
export const ArrowLeft = createIconWrapper(PhosphorArrowLeft, 'ArrowLeft')
export const ArrowRight = createIconWrapper(PhosphorArrowRight, 'ArrowRight')
export const RotateCcw = createIconWrapper(ArrowCounterClockwise, 'RotateCcw')
export const Loader2 = createIconWrapper(CircleNotch, 'Loader2')
export const CheckCircle = createIconWrapper(PhosphorCheckCircle, 'CheckCircle')
export const XCircle = createIconWrapper(PhosphorXCircle, 'XCircle')
export const ThumbsUp = createIconWrapper(PhosphorThumbsUp, 'ThumbsUp')
export const ThumbsDown = createIconWrapper(PhosphorThumbsDown, 'ThumbsDown')
export const Edit = createIconWrapper(PencilSimple, 'Edit')
export const Trash2 = createIconWrapper(TrashSimple, 'Trash2')
export const QrCode = createIconWrapper(PhosphorQrCode, 'QrCode')
export const ClipboardList = createIconWrapper(ClipboardText, 'ClipboardList')
export const FileText = createIconWrapper(PhosphorFileText, 'FileText')
export const AlertCircle = createIconWrapper(WarningCircle, 'AlertCircle')
export const AlertTriangle = createIconWrapper(PhosphorWarning, 'AlertTriangle')
export const ExclamationTriangleIcon = createIconWrapper(PhosphorWarning, 'ExclamationTriangleIcon')
export const Copy = createIconWrapper(PhosphorCopy, 'Copy')
export const Calendar = createIconWrapper(CalendarBlank, 'Calendar')
export const User = createIconWrapper(PhosphorUser, 'User')

export const Settings = createIconWrapper(Gear, 'Settings')
export const LogOut = createIconWrapper(SignOut, 'LogOut')
export const Home = createIconWrapper(House, 'Home')

// "Icon" suffixed aliases used by select and calendar components
export const CheckIcon = createIconWrapper(PhosphorCheck, 'CheckIcon')
export const ChevronDownIcon = createIconWrapper(CaretDown, 'ChevronDownIcon')
export const ChevronUpIcon = createIconWrapper(CaretUp, 'ChevronUpIcon')
export const ChevronLeftIcon = createIconWrapper(CaretLeft, 'ChevronLeftIcon')
export const ChevronRightIcon = createIconWrapper(CaretRight, 'ChevronRightIcon')
